/**
 * #40 · Generación de códigos: disparo, progreso en vivo y resultado medido.
 *
 * Qué se puede mostrar como progreso, y qué sería mentira
 * ------------------------------------------------------
 * `POST /label-orders/{id}/generate` corre **dentro de una transacción**
 * (`TenantConn`): reserva el rango de ids, crea los rollos, genera código y
 * secreto por unidad e inserta de forma conjuntista. Nada de eso es visible
 * desde afuera hasta el commit — otra sesión en READ COMMITTED no ve una sola
 * fila a medio camino. O sea: **no existe un porcentaje que se pueda leer**, y
 * no porque falte un endpoint sino porque el aislamiento de la transacción es
 * lo que garantiza que un fallo no deje etiquetas huérfanas.
 *
 * Lo que sí es real y es lo que se muestra:
 *
 *   · el estado que el servidor publica, releído cada segundo: la orden pasa a
 *     `generating` apenas reserva el rango y a `generated` al terminar;
 *   · el tiempo transcurrido, con un contador que corre;
 *   · al terminar, las métricas que devuelve la API — unidades por segundo y
 *     el desglose real por fase (`timings_ms`).
 *
 * Una barra con un porcentaje inventado sobre una duración estimada sería peor
 * que una barra indeterminada: en la demo, con 100.000 unidades en menos de 3
 * segundos, el número quedaría desfasado justo cuando alguien lo está mirando.
 *
 * El timeout del cliente se sube a 10 minutos para esta llamada. El default
 * son 20 s, pensados para el dashboard; un millón de unidades los pasa, y
 * abortar del lado del cliente no cancela nada del lado del servidor: dejaría
 * a alguien mirando un error mientras la generación termina bien.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { labelOrderKeys, rollKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { Dialog } from '../products/ui/Dialog';
import { DetailList, DetailRow } from '../products/ui/fields';

type LabelOrder = Schemas['LabelOrderOut'];
type GenerateOut = Schemas['GenerateOut'];

/** Igual que `_GENERATE_TIMEOUT` del router: 600 s. */
const GENERATE_TIMEOUT_MS = 600_000;

export interface GenerationPanelProps {
  order: LabelOrder;
  /** `true` mientras la vista esté repreguntando el estado de la orden. */
  onGeneratingChange: (generating: boolean) => void;
}

export function GenerationPanel({ order, onGeneratingChange }: GenerationPanelProps): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const generate = useMutation<GenerateOut, unknown>({
    mutationFn: () =>
      api.post<GenerateOut>(
        `/api/v1/label-orders/${String(order.id)}/generate`,
        undefined,
        { timeoutMs: GENERATE_TIMEOUT_MS },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: labelOrderKeys.all });
      // La generación crea los rollos: la lista de rollos de esta orden y la
      // general quedaron viejas en el mismo instante.
      void queryClient.invalidateQueries({ queryKey: rollKeys.all });
    },
  });

  const running = generate.isPending;

  useEffect(() => {
    onGeneratingChange(running);
  }, [running, onGeneratingChange]);

  // El contador de tiempo. Un segundo de resolución alcanza y no hace trabajar
  // al navegador cada 16 ms mientras el servidor inserta un millón de filas.
  // El reloj arranca en el `onClick`, no acá: poner el contador en cero dentro
  // del efecto sería un `setState` síncrono en el cuerpo, que es una cascada de
  // renders y además lo que prohíbe `react-hooks/set-state-in-effect`.
  useEffect(() => {
    if (!running) return;
    const start = Date.now();
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - start);
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [running]);

  const serverStatus = order.status;

  return (
    <>
      <div className="space-y-3">
        {order.status === 'draft' && !running && (
          <Button
            variant="primary"
            onClick={() => {
              generate.reset();
              setConfirming(true);
            }}
          >
            {t('labels.order.generate')}
          </Button>
        )}

        {(running || serverStatus === 'generating') && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-(--radius-card) border border-suspect/30 bg-suspect-soft p-4"
          >
            <p className="text-sm font-semibold text-suspect">
              {t('labels.order.progress.title', { units: fmt.number(order.quantity) })}
            </p>
            {/* Indeterminada a propósito: ver la cabecera del archivo. */}
            <progress className="mt-3 h-2 w-full" aria-label={t('labels.order.progress.title', { units: fmt.number(order.quantity) })} />
            <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                  {t('labels.order.progress.elapsed')}
                </dt>
                <dd className="tabular-nums">
                  {t('labels.order.progress.seconds', { seconds: Math.floor(elapsedMs / 1000) })}
                </dd>
              </div>
              <div>
                <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                  {t('labels.order.progress.serverStatus')}
                </dt>
                <dd className="font-mono">{serverStatus}</dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-ink-muted">{t('labels.order.progress.note')}</p>
          </div>
        )}

        {generate.isError && <ErrorState error={generate.error} />}

        {generate.isSuccess && (
          <div className="rounded-(--radius-card) border border-verified/30 bg-verified-soft p-4">
            <p className="text-sm font-semibold text-verified">{t('labels.order.generated.title')}</p>
            <div className="mt-3">
              <DetailList>
                <DetailRow
                  label={t('labels.order.generated.units')}
                  value={fmt.number(generate.data.units)}
                />
                <DetailRow
                  label={t('labels.order.generated.rolls')}
                  value={fmt.number(generate.data.rolls)}
                />
                <DetailRow
                  label={t('labels.order.generated.elapsed')}
                  value={t('labels.order.progress.seconds', {
                    seconds: (generate.data.elapsed_ms / 1000).toFixed(1),
                  })}
                />
                <DetailRow
                  label={t('labels.order.generated.rate')}
                  value={t('labels.order.generated.rate.value', {
                    rate: fmt.number(generate.data.units_per_second),
                  })}
                />
              </DetailList>
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-ink-muted">
              {Object.entries(generate.data.timings_ms).map(([phase, ms]) => (
                <li key={phase}>
                  {phase}: {fmt.number(ms, 1)} ms
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog
        open={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={t('labels.order.generate.title')}
        description={t('labels.order.generate.description')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('form.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirming(false);
                setElapsedMs(0);
                generate.mutate();
              }}
            >
              {t('labels.order.generate.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-ink">
          <DetailList>
            <DetailRow label={t('labels.orders.column.code')} value={order.order_code} />
            <DetailRow label={t('labels.orders.column.supplier')} value={order.supplier} />
            <DetailRow
              label={t('labels.orders.column.quantity')}
              value={fmt.number(order.quantity)}
            />
            <DetailRow
              label={t('labels.order.field.unitsPerRoll')}
              value={fmt.number(order.units_per_roll)}
            />
          </DetailList>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-muted">
            <li>{t('labels.order.generate.effect.units')}</li>
            <li>{t('labels.order.generate.effect.range')}</li>
            <li>{t('labels.order.generate.effect.key')}</li>
          </ul>
        </div>
      </Dialog>
    </>
  );
}
